import { MatchingDataGapEntity, MatchingResultPayload, MatchingSortMode, MatchingTier } from '../types';
import { buildJustificationView, JustificationSide, JustificationView } from './build-justification-view';

export interface ResultsViewPair {
  readonly ipId: number;
  readonly displayName: string;
  readonly tier: MatchingTier;
  readonly startSurroSearch: string | null;
  readonly score: number;
  readonly summary: string;
  readonly pros: string[];
  readonly cons: string[];
  readonly hardFails: string[];
  readonly hasDataGap: boolean;
  readonly proposed: boolean;
  readonly justification: JustificationView;
}

export interface ResultsViewGc {
  readonly id: number;
  readonly displayName: string;
  readonly surroAvailableInDatabase: string | null;
  readonly priorityTags: string[];
  readonly hasDataGap: boolean;
  readonly pairs: ResultsViewPair[];
}

const asArray = <T>(value: unknown): T[] => (Array.isArray(value) ? (value as T[]) : []);

const isNonEmptyList = (value: unknown): boolean => Array.isArray(value) && value.length > 0;

// Rows key AI lines by their text; the model can repeat a line, so duplicates are dropped here once.
const uniqueStrings = (value: unknown): string[] => [...new Set(asArray<string>(value))];

// A null/absent date sorts LAST in both directions (G-2), so it cannot be expressed by flipping
// the comparator — the null branch is checked before the direction is applied.
const byDate = (a: string | null, b: string | null, direction: 1 | -1): number => {
  if (!a && !b) {
    return 0;
  }

  if (!a) {
    return 1;
  }

  if (!b) {
    return -1;
  }

  return direction * (new Date(a).getTime() - new Date(b).getTime());
};

// The whole data pass for the results screen (components do zero data work). Defensive throughout:
// `result` is `GraphQLJSON` and `dataGaps`/`entityDataGaps` are unvalidated server-side, so a malformed
// payload degrades to fewer rows — never a throw, never a logged value (AC-11).
export const buildResultsView = (
  result: MatchingResultPayload | null | undefined,
  sortMode: MatchingSortMode,
): ResultsViewGc[] => {
  const gcs = asArray<MatchingResultPayload['gcs'][number]>(result?.gcs);
  const ips = asArray<MatchingResultPayload['ips'][number]>(result?.ips);
  const pairs = asArray<MatchingResultPayload['pairs'][number]>(result?.pairs);
  const proposals = asArray<MatchingResultPayload['proposals'][number]>(result?.proposals);

  const ipById = new Map(ips.map(ip => [ip.id, ip]));
  const gcById = new Map(gcs.map(gcRow => [gcRow.id, gcRow]));
  const proposedIpByGc = new Map(proposals.map(proposal => [proposal.gcId, proposal.ipId]));

  const gcIdsWithGaps = new Set(
    asArray<{ entity?: unknown; id?: unknown; fields?: unknown }>(result?.entityDataGaps)
      .filter(gap => gap?.entity === MatchingDataGapEntity.gc && isNonEmptyList(gap.fields))
      .map(gap => gap.id),
  );

  const pairsByGc = new Map<number, ResultsViewPair[]>();

  pairs.forEach(pair => {
    const ip = ipById.get(pair?.ipId);
    const gc = gcById.get(pair?.gcId);

    // A pair naming metadata that isn't there (a row deleted mid-run — D-1's accepted consequence)
    // is skipped rather than rendered nameless; an excluded pair is skipped because BA says that GC
    // and IP have already declined each other — it arrives only so the engine's coverage is total.
    if (!ip || !gc || pair.excluded) {
      return;
    }

    const sides: JustificationSide[] = [
      { role: MatchingDataGapEntity.gc, name: gc.displayName, date: gc.surroAvailableInDatabase },
      { role: MatchingDataGapEntity.ip, name: ip.displayName, tier: ip.tier, date: ip.startSurroSearch },
    ];

    const list = pairsByGc.get(pair.gcId) ?? [];

    list.push({
      ipId: ip.id,
      displayName: ip.displayName,
      tier: ip.tier,
      startSurroSearch: ip.startSurroSearch,
      score: pair.score,
      summary: pair.summary,
      pros: uniqueStrings(pair.pros),
      cons: uniqueStrings(pair.cons),
      hardFails: uniqueStrings(pair.hardFails),
      hasDataGap: isNonEmptyList(pair.dataGaps),
      proposed: proposedIpByGc.get(pair.gcId) === ip.id,
      justification: buildJustificationView({
        score: pair.score,
        weightApplied: pair.weightApplied,
        summary: pair.summary,
        pros: pair.pros,
        cons: pair.cons,
        hardFails: pair.hardFails,
        breakdown: pair.breakdown,
        skipped: pair.skipped,
        sides,
      }),
    });
    pairsByGc.set(pair.gcId, list);
  });

  return [...gcs]
    .sort((a, b) => byDate(a.surroAvailableInDatabase, b.surroAvailableInDatabase, 1) || a.id - b.id)
    .map(gc => {
      // Date order first, unconditionally: `Array.prototype.sort` is stable (ES2019), so the score
      // pass below leaves equal scores in date order for free (G-5) with no tiebreak comparator.
      const ordered = (pairsByGc.get(gc.id) ?? []).sort((a, b) => byDate(a.startSurroSearch, b.startSurroSearch, 1));

      return {
        id: gc.id,
        displayName: gc.displayName,
        surroAvailableInDatabase: gc.surroAvailableInDatabase,
        priorityTags: asArray<string>(gc.priorityTags),
        hasDataGap: gcIdsWithGaps.has(gc.id),
        pairs: sortMode === MatchingSortMode.score ? ordered.sort((a, b) => b.score - a.score) : ordered,
      };
    });
};
